import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { getQuote, formatEuro, api, fetchCatalog } from "@/lib/api";
import { generateQuotePDF } from "@/lib/pdf";
import { useAuth } from "@/lib/auth";
import { structureLabel } from "@/lib/utils";
import { AssemblyOverview } from "@/components/AssemblyOverview";
import AssemblyVisualizer3D from "@/components/AssemblyVisualizer3D";
import { Loader2, Download, ArrowLeft, ShoppingCart, CheckCircle2, Pencil } from "lucide-react";
import { AdminQuoteReport } from "@/components/AdminQuoteReport";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import CabinetVisualizer3D from "@/components/CabinetVisualizer3D";

function templateVariantOf(templateId) {
  return templateId === "template-open" ? "open"
    : templateId === "sdb-colonne-2portes" ? "sdb_column"
    : typeof templateId === "string" && templateId.startsWith("sdb-") ? "sdb"
    : "battant";
}

function sdbDrawersOf(templateId) {
  return templateId === "sdb-1tiroir" ? 1
    : templateId === "sdb-2tiroirs" ? 2
    : templateId === "sdb-3tiroirs" ? 3
    : 0;
}

// Résout couleur caisson/façades + type de poignée depuis le catalogue, pour que le
// rendu 3D (capturé dans le PDF) corresponde à la finition et à la poignée du devis.
// Fallback poignée "sdb_profil_alu" si l'id stocké est un ancien id de poignée non présent
// au catalogue (ex: "profil_line_alu") — reproduit DEFAULT_HANDLE_ID du backend (server.py).
const DEFAULT_HANDLE_ID = "sdb_profil_alu";
function finish3DProps(config, catalog) {
  if (!catalog || !config) return { carcassColor: "#B8935A", doorsColor: null, handleSvgType: null };
  const variant = templateVariantOf(config.template_id);
  const isSdbVariant = variant === "sdb" || variant === "sdb_column";
  const carcassFinish = catalog.finishes?.find((f) => f.id === config.carcass_finish_id);
  const doorsFinish = catalog.finishes?.find((f) => f.id === (config.doors_finish_id || config.carcass_finish_id));
  const handles = isSdbVariant ? catalog.sdb_handles : catalog.handles;
  const handle = handles?.find((h) => h.id === config.default_handle_id) || handles?.find((h) => h.id === DEFAULT_HANDLE_ID);
  return {
    carcassColor: carcassFinish?.swatch || "#B8935A",
    doorsColor: doorsFinish?.swatch || null,
    handleSvgType: handle?.svg_type || null,
  };
}

export default function QuoteDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [converting, setConverting] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    getQuote(id).then(setQuote).catch(() => toast.error("Devis introuvable"));
  }, [id]);

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {});
  }, []);

  const handleConvert = async () => {
    if (!quote) return;
    if (quote.status === "commande") {
      toast.info("Ce devis est déjà une commande");
      return;
    }
    setConverting(true);
    try {
      await api.post(`/quotes/${quote.id}/convert-to-order`);
      toast.success("Devis converti en commande", { description: "L'atelier a été notifié par email." });
      const fresh = await getQuote(quote.id);
      setQuote(fresh);
    } catch (e) {
      toast.error("Impossible de convertir en commande", { description: e?.response?.data?.detail || e.message });
    } finally {
      setConverting(false);
      // Nettoie ?action=convert-to-order si présent
      if (params.get("action")) {
        const next = new URLSearchParams(params);
        next.delete("action");
        setParams(next, { replace: true });
      }
    }
  };

  // Auto-convert si arrivée depuis PDF avec ?action=convert-to-order
  useEffect(() => {
    if (!quote) return;
    if (params.get("action") === "convert-to-order" && quote.status !== "commande" && !converting) {
      handleConvert();
    }
    // eslint-disable-next-line
  }, [quote]);

  if (!quote) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  const b = quote.breakdown;
  const c = quote.config;
  const mainVisual3D = finish3DProps(c, catalog);
  const totalHt = quote.total_ht_all_items ?? b.total_ht ?? b.total_ttc;
  const items = quote.items || [];
  const assembly = quote.assembly;
  const assemblyModuleB = assembly ? items.find((item) => item.id === assembly.module_b_item_id) : null;
  const mainHasDoor = (c.sections || []).some((s) => s.has_door);
  const furnitureTotalHt = quote.furniture_total_ht || totalHt - (quote.installation_fee_ht || 0) - (quote.delivery_price_ht || 0);
  const isOrder = quote.status === "commande";

  return (
    <main data-testid="quote-detail-page" className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-8 lg:py-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <Link to="/devis" data-testid="back-to-quotes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Tous les devis
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="download-pdf" onClick={async () => {
            // Laisse le temps au canvas WebGL (hors-écran) de peindre au moins une frame
            // avant la capture, évite un visuel vide en cas de clic très rapide après chargement.
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
            generateQuotePDF(quote, {
              canvasElement: document.querySelector("#cabinet-3d-main canvas"),
              itemCanvasElements: (quote.items || []).map((_, idx) => document.querySelector(`#cabinet-3d-item-${idx} canvas`)),
              assembly3dCanvas: document.querySelector('[data-testid="assembly-3d-view"] canvas'),
              catalog,
              user,
            });
          }} className="btn-primary">
            <Download className="w-4 h-4" /> PDF
          </button>
          {!isOrder && (
            <Link to={`/configurateur?edit=${quote.id}`} data-testid="edit-quote" className="btn-secondary inline-flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Modifier
            </Link>
          )}
          {!isOrder && (
            <Button
              data-testid="convert-to-order"
              onClick={handleConvert}
              disabled={converting}
              className="btn-primary"
            >
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
              Convertir en commande
            </Button>
          )}
          {isOrder && (
            <span data-testid="order-badge" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-primary/10 text-primary border border-primary/30">
              <CheckCircle2 className="w-3.5 h-3.5" /> Commande validée
            </span>
          )}
        </div>

      </div>

      {assembly && assemblyModuleB && (
        <section data-testid="assembly-project-summary" className="mb-8">
          <div className="border-l-4 border-primary bg-primary/5 px-5 py-4">
            <p className="eyebrow text-primary mb-1">Ensemble assemblé sur chantier</p>
            <p className="font-heading text-lg">2 caissons jumelés · Modules A et B conservés pour la fabrication</p>
            {assembly.continuous_plinth_mm ? <p className="mt-1 text-sm text-muted-foreground">Plinthe continue {assembly.continuous_plinth_mm} mm</p> : null}
          </div>
          <div className="mt-3 border border-border bg-card p-3 sm:p-5" data-testid="assembly-3d-section">
            <p className="eyebrow text-[10px] mb-3">Assemblage 3D</p>
            <AssemblyVisualizer3D mainConfig={c} moduleBConfig={assemblyModuleB.config} assembly={assembly} catalog={catalog} />
          </div>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div className="border border-border bg-card p-3 sm:p-5">
              <p className="eyebrow text-[10px] mb-3" data-testid="assembly-finished-label">Vue d’ensemble finie</p>
              <AssemblyOverview mainConfig={c} moduleBConfig={assemblyModuleB.config} assembly={assembly} catalog={catalog} mode="finished" />
            </div>
            <div className="border border-border bg-card p-3 sm:p-5">
              <p className="eyebrow text-[10px] mb-3" data-testid="assembly-technical-label">Vue technique atelier</p>
              <AssemblyOverview mainConfig={c} moduleBConfig={assemblyModuleB.config} assembly={assembly} catalog={catalog} mode="technical" />
            </div>
          </div>
        </section>
      )}

      <div className="paper p-8 lg:p-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-border pb-6 mb-8">
          <div>
            <p className="eyebrow mb-2">{isOrder ? "Commande" : "Devis"}</p>
            <h1 className="font-heading text-3xl sm:text-4xl">{quote.reference}</h1>
            {quote.project_name && (
              <p className="text-sm text-muted-foreground mt-2">Projet : <b className="text-foreground">{quote.project_name}</b></p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              Émis le {new Date(quote.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="text-right">
            <p className="eyebrow text-[10px]">{items.length > 0 ? `Total projet (${items.length + 1} meubles) HT` : "Total HT"}</p>
            <p className="font-heading text-4xl mt-1 text-primary tabular-nums">{formatEuro(totalHt)}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-8 mb-8">
          <div>
            <p className="eyebrow text-[10px] mb-3">{quote.user_id ? "Professionnel" : "Client"}</p>
            {quote.client.company && <p className="font-medium">{quote.client.company}</p>}
            <p className="text-sm">{quote.client.contact_name}</p>
            <p className="text-sm text-muted-foreground">{quote.client.email}</p>
            {quote.client.phone && <p className="text-sm text-muted-foreground">{quote.client.phone}</p>}
          </div>
          <div>
            {/* Liste de TOUS les meubles du devis */}
            {items.map((it, idx) => {
              const itConfig = it.config;
              const itBd = it.breakdown;
              const itHasDoor = (itConfig.sections || []).some(s => s.has_door);
              return (
                <div key={it.id || idx} className={idx > 0 ? "mt-4 pt-3 border-t border-border/40" : ""}>
                  <p className="eyebrow text-[10px] mb-1 text-primary font-bold">Meuble {idx + 1}</p>
                  <p className="font-medium text-sm">{structureLabel(it.template_name || quote.template_name, itHasDoor)}</p>
                  <p className="text-xs text-muted-foreground">{itConfig.total_width * 10} × {itConfig.height * 10} × {itConfig.depth * 10} mm · {itConfig.sections.length} section(s) · Qté {itConfig.quantity || 1}</p>
                </div>
              );
            })}
            <div className={items.length > 0 ? "mt-4 pt-3 border-t border-border/40" : ""}>
              <p className="eyebrow text-[10px] mb-1 text-primary font-bold">{items.length > 0 ? `Meuble ${items.length + 1}` : "Configuration"}</p>
              <p className="font-medium text-sm">{structureLabel(quote.template_name, mainHasDoor)}</p>
              <p className="text-xs text-muted-foreground">{c.total_width * 10} × {c.height * 10} × {c.depth * 10} mm · {c.sections.length} section(s) · Qté {c.quantity}</p>
              <dl className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                <Row k="Caisson" v={quote.carcass_finish_name} />
                {mainHasDoor && <Row k="Portes" v={quote.doors_finish_name + (b.doors_different ? " (différent)" : "")} />}
                {c.options?.under_slope_enabled && <Row k="Sous-pente" v={`${c.options.under_slope_direction === "right" ? "Droite" : "Gauche"} · H basse ${c.options.under_slope_low_height_mm} mm`} />}
              </dl>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <p className="eyebrow text-[10px] mb-4">Détail par section</p>
          <div className="space-y-2 text-sm">
            {b.sections.map((s, i) => {
              const label = s.id || String.fromCharCode(65 + i);
              const configSection = c.sections?.[i] || {};
              const parts = [];
              if (s.has_door) parts.push(s.door_specs ? `${s.door_specs.leaf_count} vantail${s.door_specs.leaf_count > 1 ? "x" : ""} ${s.door_specs.leaf_width_mm}×${s.door_specs.leaf_height_mm} mm · ${s.door_specs.opening} · ${s.door_specs.position || "toute hauteur"}` : "porte");
              if (s.drawers > 0) parts.push(`${s.drawers}× tiroir`);
              if (s.has_penderie_bar) parts.push("penderie");
              if (s.shelves_fixed) parts.push(`${s.shelves_fixed} tabl. fixe`);
              if (s.shelves_mobile) parts.push(`${s.shelves_mobile} tabl. mobile`);
              if (parts.length === 0) parts.push("ouvert");
              const shelfPositions = (configSection.elements || []).filter((element) => element.kind === "shelf_fixed" || element.kind === "shelf_mobile").map((element) => `${element.kind === "shelf_fixed" ? "fixe" : "mobile"} à ${Math.round(element.y_mm)} mm`);
              return (
                <div key={s.id} className="flex justify-between border-b border-border/50 pb-2">
                  <div>
                    <span className="font-medium">Section {label}</span>
                    <div className="text-xs text-muted-foreground">{parts.join(" · ")}</div>
                    <div className="text-xs text-muted-foreground">{c.options?.has_back_panel ? "Dos mélaminé 16 mm" : "Sans dos"}{shelfPositions.length ? ` · Tablettes : ${shelfPositions.join(", ")}` : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Lignes de majoration masquées sur le devis client */}

          <div data-testid="commercial-breakdown" className="mt-6 border border-border bg-secondary/10 p-4 space-y-3">
            <p className="eyebrow text-[10px]">Récapitulatif commercial</p>
            <div data-testid="furniture-total-row" className="flex justify-between text-sm"><span>Fourniture — caissons et éléments associés</span><span className="font-medium tabular-nums">{formatEuro(furnitureTotalHt)}</span></div>
            <div data-testid="installation-fee-row" className="flex justify-between text-sm text-muted-foreground"><span>Forfait de pose — back-office artisan</span><span className="font-medium tabular-nums text-foreground">{quote.installation_fee_applied ? formatEuro(quote.installation_fee_ht) : "Non retenu"}</span></div>
            <div data-testid="delivery-fee-row" className="flex justify-between text-sm text-muted-foreground"><span>Livraison — zones 50 km / 100 km</span><span className="font-medium tabular-nums text-foreground">{quote.delivery_applied ? formatEuro(quote.delivery_price_ht) : "Non retenue"}</span></div>
          </div>

          <div className="mt-4 rounded-xl bg-foreground text-background p-4 flex justify-between items-center">
            <span className="font-heading text-lg">Total devis HT</span>
            <span className="font-heading text-2xl tabular-nums">{formatEuro(totalHt)}</span>
          </div>

          {/* Portes coulissantes : cotes à commander séparément */}
          {b.sliding_doors_specs && (
            <div className="mt-5 p-4 rounded-xl border-2 border-primary/40 bg-primary/5" data-testid="sliding-doors-specs">
              <p className="font-heading text-sm mb-1 text-primary">
                📐 Dimensions des portes coulissantes à commander <span className="text-[10px] font-normal opacity-70">(non fournies)</span>
              </p>
              <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
                Le meuble est prévu pour recevoir des vantaux coulissants sur rail — commande à part chez le fournisseur habituel.
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-background rounded-lg p-3 border border-border">
                  <p className="eyebrow text-[9px]">Vantaux</p>
                  <p className="font-heading text-2xl mt-1 tabular-nums">{b.sliding_doors_specs.count}</p>
                  <p className="text-[9px] text-muted-foreground">recouvrement 20 mm</p>
                </div>
                <div className="bg-background rounded-lg p-3 border border-border">
                  <p className="eyebrow text-[9px]">Hauteur</p>
                  <p className="font-heading text-2xl mt-1 tabular-nums">{b.sliding_doors_specs.vantail_height_mm}</p>
                  <p className="text-[9px] text-muted-foreground">mm</p>
                </div>
                <div className="bg-background rounded-lg p-3 border border-border">
                  <p className="eyebrow text-[9px]">Largeur unitaire</p>
                  <p className="font-heading text-2xl mt-1 tabular-nums">{b.sliding_doors_specs.vantail_width_mm}</p>
                  <p className="text-[9px] text-muted-foreground">mm</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Meubles supplémentaires */}
        {items.length > 0 && (
          <div className="mt-8 pt-6 border-t border-border" data-testid="items-list">
            <p className="eyebrow text-[10px] mb-4">{assembly ? "Modules de fabrication complémentaires" : "Meubles supplémentaires du projet"}</p>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={it.id || i} data-testid={`item-${i}`} className="paper p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-heading text-base">{assemblyModuleB?.id === it.id ? "Module B" : `Meuble ${i + 1}`}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {structureLabel(it.template_name, (it.config.sections || []).some((s) => s.has_door))} · {it.config.total_width * 10}×{it.config.height * 10}×{it.config.depth * 10} mm · {it.config.sections.length} section(s)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Caisson : {it.carcass_finish_name}{(it.config.sections || []).some((s) => s.has_door) ? ` · Portes : ${it.doors_finish_name}` : ""}
                    </p>
                    {/* sous-pente surcharge masquée sur le devis client */}
                  </div>
                  <span className="tabular-nums font-medium shrink-0">{formatEuro(it.breakdown.total_ht ?? it.breakdown.total_ttc)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-primary text-primary-foreground p-4 flex justify-between items-center">
              <span className="font-heading text-lg">TOTAL PROJET ({items.length + 1} meubles) HT</span>
              <span className="font-heading text-2xl tabular-nums" data-testid="total-project-ht">{formatEuro(totalHt)}</span>
            </div>
          </div>
        )}

        {(() => {
          const sketches = [
            { label: `Meuble ${items.length + 1}`, sketch: c.options?.customer_structure_sketch },
            ...items.map((it, i) => ({ label: `Meuble ${i + 1}`, sketch: it.config?.options?.customer_structure_sketch })),
          ].filter((entry) => entry.sketch?.data_url);
          if (sketches.length === 0) return null;
          return (
            <div data-testid="quote-customer-sketch" className="mt-8 pt-6 border-t border-border">
              <p className="eyebrow text-[10px] mb-2">Croquis / relevé client — référence atelier</p>
              <p className="text-xs text-muted-foreground mb-4">Document fourni par le client, joint à la fiche de fabrication pour contrôle des cotes sous-pente.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {sketches.map((entry, i) => (
                  <figure key={i} data-testid={`quote-customer-sketch-${i}`} className="border border-border bg-card p-3">
                    <img src={entry.sketch.data_url} alt={`Croquis client — ${entry.label}`} className="w-full h-auto max-h-80 object-contain bg-background" />
                    <figcaption className="mt-2 text-[11px] text-muted-foreground truncate">{entry.label} · {entry.sketch.name}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          );
        })()}

        {quote.client.notes && (
          <div data-testid="quote-client-notes" className="mt-8 pt-6 border-t border-border">
            <p className="eyebrow text-[10px] mb-2">Commentaire du client</p>
            <p className="text-sm bg-secondary/10 border border-border rounded-lg p-4 whitespace-pre-wrap">{quote.client.notes}</p>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-8 italic">
          Devis valable 30 jours · Prix HT · Livraison de 2 à 4 semaines
        </p>
      </div>

      {user?.role === "admin" && (
        <AdminQuoteReport quote={quote} />
      )}

      {/* Rendu 3D masqué (hors-écran) : capturé en PNG au clic "PDF" pour l'intégrer
          comme visuel dans le document téléchargé (le tableau technique par section
          reste inchangé, seul ce visuel remplace l'ancien rendu 2D du PDF). */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: "900px",
          height: "700px",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <div id="cabinet-3d-main" style={{ width: "900px", height: "700px" }}>
          <CabinetVisualizer3D
            totalWidth={c.total_width}
            height={c.height}
            depth={c.depth + (c.options?.prepared_for_coulissant ? 10 : 0)}
            sections={c.sections}
            carcassColor={mainVisual3D.carcassColor}
            doorsColor={mainVisual3D.doorsColor}
            doorsFinishId={c.doors_finish_id}
            doorsDifferent={!!c.doors_finish_id && c.doors_finish_id !== c.carcass_finish_id}
            showDoors={true}
            options={c.options || null}
            templateVariant={templateVariantOf(c.template_id)}
            sdbDrawers={sdbDrawersOf(c.template_id)}
            handleSvgType={mainVisual3D.handleSvgType}
            printMode
          />
        </div>
        {(quote.items || []).map((it, idx) => {
          const itemVisual3D = finish3DProps(it.config, catalog);
          return (
          <div key={it.id || idx} id={`cabinet-3d-item-${idx}`} style={{ width: "900px", height: "700px" }}>
            <CabinetVisualizer3D
              totalWidth={it.config.total_width}
              height={it.config.height}
              depth={it.config.depth + (it.config.options?.prepared_for_coulissant ? 10 : 0)}
              sections={it.config.sections}
              carcassColor={itemVisual3D.carcassColor}
              doorsColor={itemVisual3D.doorsColor}
              doorsFinishId={it.config.doors_finish_id}
              doorsDifferent={!!it.config.doors_finish_id && it.config.doors_finish_id !== it.config.carcass_finish_id}
              showDoors={true}
              options={it.config.options || null}
              templateVariant={templateVariantOf(it.config.template_id)}
              sdbDrawers={sdbDrawersOf(it.config.template_id)}
              handleSvgType={itemVisual3D.handleSvgType}
              printMode
            />
          </div>
          );
        })}
      </div>
    </main>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function PriceLine({ k, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="tabular-nums font-medium">{formatEuro(v)}</span>
    </div>
  );
}